import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CopyIcon, ArrowRightIcon, CheckCircleIcon } from 'lucide-react';
import { AcademicYearSelector } from './AcademicYearSelector';
interface RecreateClassroomsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (yearId: string) => void;
  count: number;
  academicYears: any[];
  currentYearId: string;
}
export const RecreateClassroomsModal: React.FC<RecreateClassroomsModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  count,
  academicYears,
  currentYearId
}) => {
  // Default to the next year if available, otherwise current
  const [selectedYearId, setSelectedYearId] = useState(() => {
    const currentIndex = academicYears.findIndex(y => y.id === currentYearId);
    if (currentIndex !== -1 && currentIndex > 0) {
      return academicYears[currentIndex - 1].id; // Assuming sorted desc or newer is first
    }
    return currentYearId;
  });
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-2089">
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-2090">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="bg-surface-elevated rounded-2xl shadow-2xl max-w-lg w-full border border-custom overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-2091">
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b border-custom bg-gradient-to-r from-[#00B75F]/5 to-transparent" data-id="element-2092">
            <div className="flex items-start gap-4" data-id="element-2093">
              <div className="w-12 h-12 bg-[#00B75F] bg-opacity-10 text-[#00B75F] rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-2094">
                <CopyIcon size={24} data-id="element-2095" />
              </div>
              <div data-id="element-2096">
                <h2 className="text-lg font-bold text-text-primary mb-1" data-id="element-2097">
                  Recreate Classrooms?
                </h2>
                <p className="text-sm text-text-secondary" data-id="element-2098">
                  Set up {count} classrooms for the new academic year.
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-surface transition-colors" data-id="element-2099">
              <XIcon size={20} className="text-text-secondary" data-id="element-2100" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6" data-id="element-2101">
            <div className="space-y-4" data-id="element-2102">
              <label className="block text-sm font-semibold text-text-primary" data-id="element-2103">
                Select Academic Year
              </label>
              <div className="bg-surface rounded-xl p-1 border border-custom" data-id="element-2104">
                <AcademicYearSelector years={academicYears} selectedYearId={selectedYearId} onYearChange={setSelectedYearId} data-id="element-2105" />
              </div>
              <p className="text-xs text-text-tertiary" data-id="element-2106">
                Classrooms will be created in this academic year.
              </p>
            </div>

            <div className="bg-surface rounded-xl p-4 border border-custom space-y-3" data-id="element-2107">
              <h3 className="text-sm font-semibold text-text-primary" data-id="element-2108">
                What will be copied:
              </h3>
              <ul className="space-y-2" data-id="element-2109">
                <li className="flex items-center gap-2 text-sm text-text-secondary" data-id="element-2110">
                  <CheckCircleIcon size={16} className="text-[#00B75F]" data-id="element-2111" />
                  Classroom names, grades, and sections
                </li>
                <li className="flex items-center gap-2 text-sm text-text-secondary" data-id="element-2112">
                  <CheckCircleIcon size={16} className="text-[#00B75F]" data-id="element-2113" />
                  Educator assignments
                </li>
                <li className="flex items-center gap-2 text-sm text-text-secondary opacity-50" data-id="element-2114">
                  <XIcon size={16} className="text-text-tertiary" data-id="element-2115" />
                  Student enrollments (start fresh)
                </li>
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 bg-surface border-t border-custom" data-id="element-2116">
            <button onClick={onClose} className="px-5 py-2.5 border border-custom text-text-primary rounded-xl font-semibold hover:bg-surface-elevated transition-colors" data-id="element-2117">
              Skip Recreation
            </button>
            <button onClick={() => {
            onConfirm(selectedYearId);
            onClose();
          }} className="px-5 py-2.5 bg-[#00B75F] text-white rounded-xl font-semibold hover:bg-[#00a055] transition-colors shadow-sm flex items-center gap-2" data-id="element-2118">
              Recreate Classrooms
              <ArrowRightIcon size={16} data-id="element-2119" />
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
};